import { apiFetchJSON } from '../../core/api.js';
import { getTg } from '../../core/telegram.js';
import { getIsOwnerApp } from '../../app/app-state.js';
import type { OwnerChatMessage, OwnerChatSendResponse } from '../../types/api.js';
import {
  getOwnerChatActiveConversationUserId,
  getOwnerChatActiveCustomerUserId,
  getOwnerChatActiveOrderId,
  getOwnerChatActiveOrderTitle,
  getOwnerChatMode,
  setOwnerChatSuppressKeyboardScroll
} from './ui-state.js';
import {
  getOwnerChatViewerId,
  getOwnerChatViewerUsername,
  ownerChatNotifySendFailed,
  ownerChatInputEl,
  ownerChatSendBtn
} from './dom.js';
import {
  getOwnerChatPendingPhoto,
  getOwnerChatIsSending,
  setOwnerChatIsSending,
  setOwnerChatPendingOutgoingId,
  getOwnerChatLastSeenId,
  setOwnerChatLastSeenId
} from './session.js';
import { ownerChatClearPendingPhoto, ownerChatReadPhotoBase64, isOwnerChatPendingPhotoReady, ownerChatSyncSendButtonState } from './attach.js';
import {
  appendOwnerChatMessages,
  removeOwnerChatMessageById,
  upgradePendingOwnerChatMessage,
  updateOwnerChatMessageInDom
} from './messages.js';
import { ownerChatPhotoProgressByMsgId, xhrAuthedJson } from './photo-progress.js';
import {
  cancelOwnerChatEdit,
  getOwnerChatEditingMessageId
} from './edit-mode.js';
import { syncOwnerChatInputHeight } from './composer-input.js';

async function submitOwnerChatEdit(messageId: string, text: string): Promise<boolean> {
  const conversationUserId = getOwnerChatActiveConversationUserId();
  if (!conversationUserId) return false;

  const data = await apiFetchJSON<OwnerChatSendResponse>('/api/owner-chat/edit-message', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationUserId, messageId, text })
  }).catch(function () {
    return null;
  });

  if (!data || !data.ok || !data.message) {
    ownerChatNotifySendFailed('text');
    return false;
  }

  updateOwnerChatMessageInDom(data.message);
  cancelOwnerChatEdit();
  return true;
}

export async function sendOwnerChatMessage() {
  if (!getTg()) return;
  if (!getOwnerChatViewerId()) return;
  if (getOwnerChatIsSending()) return;
  if (!getOwnerChatActiveConversationUserId()) return;

  const rawText = ownerChatInputEl ? ownerChatInputEl.value : '';
  const text = rawText.trim();
  const editingMessageId = getOwnerChatEditingMessageId();

  if (editingMessageId) {
    if (!text) return;
    setOwnerChatIsSending(true);
    if (ownerChatSendBtn) ownerChatSendBtn.disabled = true;
    ownerChatSyncSendButtonState();
    try {
      await submitOwnerChatEdit(editingMessageId, text);
    } finally {
      setOwnerChatIsSending(false);
      ownerChatSyncSendButtonState();
    }
    return;
  }

  const pendingPhoto = getOwnerChatPendingPhoto();
  const pendingFile = pendingPhoto && pendingPhoto.file ? pendingPhoto.file : null;
  const pendingPhotoPreview =
    pendingPhoto && pendingPhoto.objectUrl ? pendingPhoto.objectUrl : '';
  const hasPhoto = !!pendingFile;
  if (!text && !hasPhoto) return;
  if (hasPhoto && !isOwnerChatPendingPhotoReady()) return;

  const savedText = text;
  let pendingMsgId = -Date.now();
  const optimistic: OwnerChatMessage = {
    id: pendingMsgId,
    fromUserId: getOwnerChatViewerId(),
    fromRole: getIsOwnerApp() ? 'owner' : 'user',
    text: savedText,
    createdAt: Date.now(),
  };

  if (ownerChatInputEl) {
    ownerChatInputEl.value = '';
    syncOwnerChatInputHeight();
  }
  if (hasPhoto) ownerChatClearPendingPhoto(true, false);

  appendOwnerChatMessages([optimistic], {
    sendIn: true,
    instantScroll: true,
    deferScroll: true,
    photoLoadingIds: hasPhoto ? new Set([String(pendingMsgId)]) : undefined,
  });
  setOwnerChatPendingOutgoingId(pendingMsgId);

  setOwnerChatSuppressKeyboardScroll(true);
  setTimeout(function () {
    setOwnerChatSuppressKeyboardScroll(false);
  }, 1100);

  setOwnerChatIsSending(true);
  if (ownerChatSendBtn) ownerChatSendBtn.disabled = true;
  ownerChatSyncSendButtonState();

  try {
    let photoPayload: {
      mime: string;
      base64: string;
      width: number | null;
      height: number | null;
    } | null = null;
    if (hasPhoto && pendingPhoto && pendingPhoto.packed) {
      const photoBlock = ownerChatPhotoProgressByMsgId(pendingMsgId);
      if (photoBlock) photoBlock.setProgress(35);
      photoPayload = pendingPhoto.packed;
    } else if (hasPhoto && pendingFile) {
      const photoBlock = ownerChatPhotoProgressByMsgId(pendingMsgId);
      const packed = await ownerChatReadPhotoBase64(pendingFile, function (pct) {
        if (photoBlock) photoBlock.setProgress(Math.round(pct * 0.35));
      });
      if (photoBlock) photoBlock.setProgress(35);
      photoPayload = {
        mime: packed.mime,
        base64: packed.base64,
        width: packed.width,
        height: packed.height,
      };
    }

    const mode = getOwnerChatMode();
    const orderId = getOwnerChatActiveOrderId();
    const orderTitle = getOwnerChatActiveOrderTitle();
    const customerUserId = getOwnerChatActiveCustomerUserId();

    const payload = getIsOwnerApp()
      ? mode === 'order' && orderId && customerUserId
        ? {
            toUserId: customerUserId,
            text: savedText,
            username: getOwnerChatViewerUsername(),
            photo: photoPayload,
            threadKind: 'order',
            orderId,
            orderTitle
          }
        : {
            toUserId: getOwnerChatActiveConversationUserId(),
            text: savedText,
            username: getOwnerChatViewerUsername(),
            photo: photoPayload,
            threadKind: 'consult'
          }
      : mode === 'order' && orderId
        ? {
            text: savedText,
            username: getOwnerChatViewerUsername(),
            photo: photoPayload,
            threadKind: 'order',
            orderId,
            orderTitle
          }
        : {
            text: savedText,
            username: getOwnerChatViewerUsername(),
            photo: photoPayload,
            threadKind: 'consult'
          };

    const payloadBody = JSON.stringify(payload);
    const photoBlock = hasPhoto ? ownerChatPhotoProgressByMsgId(pendingMsgId) : null;

    const data = hasPhoto
      ? await xhrAuthedJson('/api/owner-chat/send', payloadBody, function (pct) {
          if (photoBlock) photoBlock.setProgress(35 + Math.round(pct * 0.6));
        })
          .then(function (result) {
            return result.ok && result.data && typeof result.data === 'object'
              ? (result.data as OwnerChatSendResponse)
              : null;
          })
          .catch(function () {
            return null;
          })
      : await apiFetchJSON<OwnerChatSendResponse>('/api/owner-chat/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payloadBody,
        }).catch(() => null);

    if (!data || !data.ok || !data.message) {
      if (pendingPhotoPreview) {
        try { URL.revokeObjectURL(pendingPhotoPreview); } catch (_) {}
      }
      if (photoBlock) photoBlock.fail();
      removeOwnerChatMessageById(pendingMsgId);
      setOwnerChatPendingOutgoingId(null);
      if (ownerChatInputEl) {
        ownerChatInputEl.value = savedText;
        syncOwnerChatInputHeight();
      }
      ownerChatNotifySendFailed(hasPhoto ? 'photo' : 'text');
      return;
    }

    if (photoBlock) photoBlock.setProgress(100);

    const message = data.message;
    setOwnerChatLastSeenId(Number(message.id || getOwnerChatLastSeenId()));
    if (!upgradePendingOwnerChatMessage(pendingMsgId, message, pendingPhotoPreview || undefined)) {
      appendOwnerChatMessages([message], { sendIn: true, instantScroll: true, deferScroll: true });
    }
    setOwnerChatPendingOutgoingId(null);
  } catch (_) {
    if (pendingPhotoPreview) {
      try { URL.revokeObjectURL(pendingPhotoPreview); } catch (_) {}
    }
    const failedBlock = hasPhoto ? ownerChatPhotoProgressByMsgId(pendingMsgId) : null;
    if (failedBlock) failedBlock.fail();
    removeOwnerChatMessageById(pendingMsgId);
    setOwnerChatPendingOutgoingId(null);
    if (ownerChatInputEl) {
      ownerChatInputEl.value = savedText;
      syncOwnerChatInputHeight();
    }
    ownerChatNotifySendFailed(hasPhoto ? 'photo' : 'text');
  } finally {
    setOwnerChatIsSending(false);
    ownerChatSyncSendButtonState();
  }
}
